import React, { useCallback } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { ActiveContent } from '../../../../types/activeContent';
import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { useRequeueTrack } from '../../../hooks/useRequeueTrack';
import { setInterimSupportingContent, setSupportingContent } from '../../../state/AudioPlayerState';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';
import type {
	ConfluenceLength,
	ConfluenceTone,
} from '../__generated__/AudioContainerQuery.graphql';

type AnalyticsAttributes = {
	length?: ConfluenceLength | null;
	tone?: ConfluenceTone | null;
	areSourcesDiverged?: boolean;
};

export type RefetchAudioButtonProps = {
	buttonText: MessageDescriptor;
	source: 'customizePanel' | 'sourcesPanel';
	shouldRequeue?: boolean;
	supportingContent?: ActiveContent['supportingContent'];
	isDisabled?: boolean;
	analyticsAttributes?: AnalyticsAttributes;
};

/**
 * Button component that handles refetching audio with optional supporting content updates
 * and playlist management.
 */
export const RefetchAudioButton = ({
	buttonText,
	shouldRequeue = false,
	supportingContent = [],
	isDisabled = false,
	analyticsAttributes,
}: RefetchAudioButtonProps) => {
	const { fetchAudio, audioRef } = useAudioPlayerStreamingContext();
	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { setActiveMenu } = useAudioPlayerStore();
	const isLoading = streamingState.state === AudioStreamingState.LOADING;
	const { formatMessage, locale } = useIntl();
	const { sendUIEvent } = usePlatformInstrumentation();

	const startNewAudio = useCallback(() => {
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'audioRegenerate',
			source: 'audioPlayer',
			attributes: {
				version: 'v3',
				audioLocale: locale,
				...analyticsAttributes,
			},
		});
		fetchAudio();
	}, [sendUIEvent, locale, analyticsAttributes, fetchAudio]);

	const stopExistingAudio = useCallback(() => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
	}, [audioRef]);

	const { requeueTrack } = useRequeueTrack({
		analyticsAttributes,
		refetchAudio: true,
	});

	const handleClick = useCallback(async () => {
		if (shouldRequeue) {
			await requeueTrack(supportingContent);
		} else {
			stopExistingAudio();

			if (supportingContent.length > 0) {
				setSupportingContent(supportingContent);
				setInterimSupportingContent([]);
			}

			startNewAudio();
			// Return to the audio player home screen
			setActiveMenu(undefined);
		}
	}, [
		requeueTrack,
		shouldRequeue,
		stopExistingAudio,
		supportingContent,
		startNewAudio,
		setActiveMenu,
	]);

	return (
		<Button
			testId="refetch-audio-button"
			shouldFitContainer
			appearance="primary"
			onClick={handleClick}
			isLoading={isLoading}
			isDisabled={isDisabled}
		>
			{formatMessage(buttonText)}
		</Button>
	);
};
